/**
 * PTM — Payment Terminal mode shared contracts
 * (docs/PAYTERM-PAYMENT-TERMINAL-MODE-PLAN.md).
 *
 * The payterm surface is a pay-only POS: rooms → tables → settle. It lives in
 * this folder and imports the core POS pieces unchanged; its only footprint in
 * core code is the route registration + device redirect (app.routes.ts), the
 * device marker (StorageKey), the mode lists, and two URL one-liners — the
 * settle screen is "busy" for satellite-follow, and `/pos/pt/…` is not a
 * scheduled-order chime screen (PTM-4 / PTM-6).
 */

/** Device OperatingMode value for a payment-terminal handheld (backend allow-list + mode lists). */
export const PAYTERM_OPERATING_MODE = 'PayTerm';

/** URL segment under `/pos/s/:posStoreCode/` that hosts the payterm PIN entry. */
export const PAYTERM_SEGMENT = 'pt';

/**
 * PTM-6 — app route that hosts the payterm WORKING screens (tables, settle).
 * Deliberately NOT under `/pos/s/<code>/`: the Flutter edge shell shows its
 * Exit ✕ and Settings ⚙ on every `/pos/s/<code>[/…]` page, and the operator
 * should only see them on the PIN screen — the same split the core POS uses
 * (`/pos/s/<code>` PIN → `/pos/terminal`). The store code the working screens
 * need comes from POS_STANDALONE_CODE, which the entry stamps.
 */
export const PAYTERM_SCREENS_PATH = 'pos/pt';

/**
 * Store setting `PayTermSalesPersonMode`. Only meaningful when sign-in is NOT
 * required per bill; null / unknown on the wire reads as 'off'.
 */
export type PaytermSalesPersonMode = 'off' | 'optional' | 'required';

/** Normalised payterm store settings. */
export interface PaytermSettings {
  /**
   * `PayTermRequireSignIn` — true (the default, also when the field is null or
   * absent): every bill is settled by the PIN-signed-in user and the terminal
   * locks after each settled bill and after 2 minutes idle. false: an allowed
   * person (pos.session.open) unlocks the terminal once for the shift.
   */
  requireSignIn: boolean;
  /** Effective sales-person mode; always 'off' while requireSignIn is true. */
  salesPersonMode: PaytermSalesPersonMode;
}

export const DEFAULT_PAYTERM_SETTINGS: PaytermSettings = {
  requireSignIn: true,
  salesPersonMode: 'off',
};

/**
 * Reads the two settings from either `GET stores/{id}/settings` or the
 * pre-login `GET pos/store/{code}` payload (both camelCase). Fail-safe: any
 * missing / unexpected value lands on the accountable default (PIN required,
 * no sales person).
 */
export function normalisePaytermSettings(raw: unknown): PaytermSettings {
  const src = (raw ?? {}) as Record<string, unknown>;
  const requireSignIn = src['payTermRequireSignIn'] !== false;
  const mode = typeof src['payTermSalesPersonMode'] === 'string'
    ? (src['payTermSalesPersonMode'] as string).trim().toLowerCase()
    : '';
  const salesPersonMode: PaytermSalesPersonMode = requireSignIn
    ? 'off'
    : mode === 'optional' || mode === 'required' ? mode : 'off';
  return { requireSignIn, salesPersonMode };
}

/** Idle lock window while sign-in is required per bill (owner decision D2). */
export const PAYTERM_IDLE_LOCK_MS = 120_000;

/** Staff row from `GET pos/staff/waiters` — the sales-person picker source. */
export interface PaytermStaffMember {
  id: string;
  fullName: string;
  roles: string[];
}

/** The payterm PIN entry, `/pos/s/<code>/pt`, as router commands (edge controls shown). */
export function paytermEntryCommands(storeCode: string): string[] {
  return ['/pos/s', storeCode, PAYTERM_SEGMENT];
}

/** A payterm working screen, `/pos/pt/<...rest>`, as router commands (no edge controls). */
export function paytermScreenCommands(...rest: string[]): string[] {
  return ['/' + PAYTERM_SCREENS_PATH, ...rest];
}

/**
 * True when `url` (path, query ignored) is inside the payterm surface — the
 * entry (`/pos/s/<code>/pt…`) or a working screen (`/pos/pt…`).
 */
export function isPaytermUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].split('#')[0];
  return /^\/pos\/s\/[^/]+\/pt(\/|$)/.test(path) || /^\/pos\/pt(\/|$)/.test(path);
}

/** A working screen, `/pos/pt/<something>`. */
const SCREEN_URL = /^\/pos\/pt\/[^/?#]/;
/** A pre-PTM-6 working screen, `/pos/s/<code>/pt/<something>`; group 1 = code, group 2 = the rest. */
const LEGACY_SCREEN_URL = /^\/pos\/s\/([^/?#]+)\/pt(\/[^?#].*)$/;

/**
 * PTM-6 — the entry's `returnUrl` filter: where a signed-in operator may be
 * sent after the PIN. Only a payterm working screen, never a core route, never
 * off-site. A `/pos/pt/…` screen carries no store code — it runs for the store
 * the entry stamps, i.e. `code` — so it is accepted as is. A pre-PTM-6
 * `/pos/s/<code>/pt/…` one is accepted only for THIS store and is rewritten to
 * its `/pos/pt/…` form (the old path now lands on the entry itself).
 * Returns null for anything else.
 */
export function paytermSafeReturnUrl(url: string | null | undefined, code: string): string | null {
  if (!url || url.startsWith('//')) return null;
  if (SCREEN_URL.test(url)) return url;
  const m = LEGACY_SCREEN_URL.exec(url);
  if (!m) return null;
  let urlCode = m[1];
  try { urlCode = decodeURIComponent(urlCode); } catch { /* keep raw */ }
  if (urlCode.toLowerCase() !== code.trim().toLowerCase()) return null;
  return '/' + PAYTERM_SCREENS_PATH + m[2];
}
